import type { ChatStreamEvent } from "~/ai-chat/data-source/stream-chat-message";
import type { ChatMessage, ChatToolCall } from "~/ai-chat/model/chat-message";

type Paint = (message: ChatMessage) => void;
type ToolCallEvent = Extract<ChatStreamEvent, { type: "toolCall" }>;

/**
 * One streaming run's assistant bubble. It is created on the first delta so an empty bubble never
 * flashes; later deltas grow it. The bubble owns its text and tool calls and hands `paint` the whole
 * message each time, so wherever the transcript it lands in has been since (cleared on a switch,
 * put back on return) the next paint restores it in full. Tool calls that start (and finish) before
 * it exists are held and land on it when it's created, or on an empty bubble if the run ends without
 * text (see flush).
 */
export function createAssistantBubble(paint: Paint) {
  let message: ChatMessage | null = null;
  let pendingToolCalls: ChatToolCall[] = [];
  const commit = (next: ChatMessage) => {
    message = next;
    paint(next);
  };
  const create = (content: string) => {
    const toolCalls = pendingToolCalls;
    pendingToolCalls = [];
    commit({
      kind: "message",
      id: crypto.randomUUID(),
      role: "assistant",
      content,
      createdAt: new Date(),
      ...(toolCalls.length > 0 ? { toolCalls } : {}),
    });
  };
  const updateCalls = (update: (calls: ChatToolCall[]) => ChatToolCall[]) => {
    if (message === null) {
      pendingToolCalls = update(pendingToolCalls);
      return;
    }
    commit({ ...message, toolCalls: update(message.toolCalls ?? []) });
  };
  return {
    appendDelta(content: string) {
      if (message === null) {
        create(content);
        return;
      }
      commit({ ...message, content: message.content + content });
    },
    recordToolCall(event: ToolCallEvent) {
      const { args } = event;
      if (event.status === "started") {
        if (args !== undefined) updateCalls((calls) => [...calls, { tool: event.name, args }]);
        return;
      }
      const { outcome } = event;
      if (outcome === undefined) return;
      // The latest unfinished start of this tool, so a sibling that already finished keeps its own outcome.
      updateCalls((calls) =>
        replaceLast(
          calls,
          (call) => call.tool === event.name && !call.outcome,
          (call) => ({ ...call, outcome }),
        ),
      );
    },
    // Tool calls ran but no text came (stopped, or paused for approval): give them an empty bubble so
    // they stay visible. A no-op once a bubble exists.
    flush() {
      if (message === null && pendingToolCalls.length > 0) create("");
    },
    // A gated call's start precedes its approval pause, but it hasn't run: it runs on the approved
    // resume. Drop the latest start of that tool (by name, so a sibling that did run is kept).
    dropGatedToolCall(tool: string) {
      updateCalls((calls) =>
        replaceLast(
          calls,
          (call) => call.tool === tool,
          () => null,
        ),
      );
    },
  };
}

function replaceLast(
  calls: ChatToolCall[],
  matches: (call: ChatToolCall) => boolean,
  replace: (call: ChatToolCall) => ChatToolCall | null,
): ChatToolCall[] {
  const index = calls.map(matches).lastIndexOf(true);
  if (index === -1) return calls;
  const replacement = replace(calls[index]);
  return [...calls.slice(0, index), ...(replacement ? [replacement] : []), ...calls.slice(index + 1)];
}
